<script setup lang="ts">
import type { FormKitFrameworkContext } from "@formkit/core";
import { plainTextToHtml, toLines } from "@shared/utils/matchEditorSpacing";
import { mergeAttributes } from "@tiptap/core";
import BulletList from "@tiptap/extension-bullet-list";
import BaseHeading from "@tiptap/extension-heading";
import OrderedList from "@tiptap/extension-ordered-list";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { Fragment, type Node, Slice } from "@tiptap/pm/model";
import { StarterKit } from "@tiptap/starter-kit";
import { EditorContent, useEditor } from "@tiptap/vue-3";
import { toRef, watch } from "vue";

const props = defineProps<{
  context: FormKitFrameworkContext;
}>();

const contextValue = toRef(() => props.context._value);

// Adding inline styles to the html created by the editor so that the styles are preserved on the Marketplace
type Levels = 1 | 2 | 3;
const styles: Record<Levels, string> = {
  1: "font-size: 3em; font-weight: bold; line-height: 1.2;",
  2: "font-size: 2em; line-height: 1.3;",
  3: "font-size: 1.5em; line-height: 1.4;",
};

const customHeading = BaseHeading.configure({ levels: [1, 2, 3] }).extend({
  renderHTML({ node, HTMLAttributes }) {
    const hasLevel = this.options.levels.includes(node.attrs.level);
    const level: Levels = hasLevel ? node.attrs.level : this.options.levels[0];

    return [
      `h${level}`,
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        style: `${styles[level]}`,
      }),
      0,
    ];
  },
});

const customBulletList = BulletList.configure().extend({
  renderHTML() {
    return [
      "ul",
      mergeAttributes(this.options.HTMLAttributes, {
        style: "list-style: circle; padding-left: 2rem;",
      }),
      0,
    ];
  },
});

const customOrderedList = OrderedList.configure().extend({
  renderHTML() {
    return [
      "ol",
      mergeAttributes(this.options.HTMLAttributes, {
        style: "list-style: lower-roman; padding-left: 2rem;",
      }),
      0,
    ];
  },
});

// For image uploading
// https://gist.github.com/slava-vishnyakov/16076dff1a77ddaca93c4bccd4ec4521

const editor = useEditor({
  // ProseMirror drops blank lines on paste.
  editorProps: {
    clipboardTextParser: (text, $context) => {
      const { schema } = $context.doc.type;

      return new Slice(
        Fragment.from(
          toLines(text).map((line) =>
            schema.nodes.paragraph!.create(
              null,
              line.trim() ? schema.text(line, $context.marks()) : null,
            ),
          ),
        ),
        1,
        1,
      );
    },
    transformPastedHTML: (html) =>
      html
        .replace(/<div[^>]*>\s*<br[^>]*>\s*<\/div>/gi, "<p><br></p>")
        .replace(
          /(?<=<\/p>\s*(?:<br[^>]*>\s*)*)<br[^>]*>(?=[\s\S]*?<p)/gi,
          "<p></p>",
        ),
    handlePaste: (view, event, slice) => {
      const html = event.clipboardData?.getData("text/html");
      if (!html || html.includes("data-pm-slice")) return false;

      const blocks: Node[] = [];
      slice.content.forEach((node) => blocks.push(node));

      const blankLinesBefore: number[] = [];
      let blankLines = 0;
      toLines(event.clipboardData?.getData("text/plain") ?? "").forEach(
        (line) => {
          if (line.trim()) {
            blankLinesBefore.push(blankLines);
            blankLines = 0;
          } else {
            blankLines++;
          }
        },
      );

      const blockLineCounts = blocks.map(
        (node) =>
          node
            .textBetween(0, node.content.size, "\n", "\n")
            .split("\n")
            .filter((line) => line.trim()).length,
      );

      if (
        blocks.length < 2 ||
        blocks.some((node) => !node.isBlock) ||
        blockLineCounts.includes(0) ||
        blockLineCounts.reduce((sum, count) => sum + count, 0) !==
          blankLinesBefore.length ||
        blankLinesBefore.every((count) => count === 0)
      ) {
        return false;
      }

      let lineIndex = 0;
      const separated = blocks.flatMap((node, index) => {
        const gap = index === 0 ? 0 : blankLinesBefore[lineIndex]!;
        lineIndex += blockLineCounts[index]!;

        return [
          ...Array.from({ length: gap }, () =>
            view.state.schema.nodes.paragraph!.create(),
          ),
          node,
        ];
      });

      view.dispatch(
        view.state.tr
          .replaceSelection(
            new Slice(Fragment.from(separated), slice.openStart, slice.openEnd),
          )
          .scrollIntoView()
          .setMeta("paste", true)
          .setMeta("uiEvent", "paste"),
      );

      return true;
    },
  },
  extensions: [
    StarterKit.configure({
      // we will manually add the following extensions
      heading: false,
      bulletList: false,
      orderedList: false,
      link: {
        HTMLAttributes: {
          class: "underline cursor-pointer",
        },
      },
    }),
    customHeading,
    customBulletList,
    customOrderedList,
    Placeholder,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
  ],
  content: plainTextToHtml(contextValue.value ?? ""),
  onUpdate({ editor }) {
    props.context.node.input(editor.getHTML());
  },
});

// Tiptap snapshots `content` at mount; follow external changes here.
watch([editor, contextValue], ([editorInstance, value]) => {
  if (!editorInstance) return;

  const next = plainTextToHtml(value ?? "");

  if (editorInstance.getHTML() === next) return;

  editorInstance.commands.setContent(next, { emitUpdate: false });
});

const setLink = (e: Event) => {
  e.preventDefault();

  const previousUrl = editor.value?.getAttributes("link").href;
  const url = window.prompt("URL", previousUrl);

  // cancelled
  if (url === null) {
    return;
  }

  // empty
  if (url === "") {
    editor.value?.chain().focus().extendMarkRange("link").unsetLink().run();

    return;
  }

  // update link
  editor.value
    ?.chain()
    .focus()
    .extendMarkRange("link")
    .setLink({ href: url, target: "_blank" })
    .run();
};

const unsetLink = (e: Event) => {
  e.preventDefault();
  editor.value?.chain().focus().unsetLink().run();
};
</script>

<template>
  <div class="w-full -m-px">
    <div
      v-if="editor"
      class="flex flex-wrap items-center gap-0.5 px-2 py-1.5 -mx-4 -mt-2 mb-4 border-b border-gray-200 dark:border-gray-600"
    >
      <button
        type="button"
        class="toolbar-btn"
        :class="{ 'is-active': editor.isActive('bold') }"
        title="Bold"
        @click="editor?.chain().focus().toggleBold().run()"
      >
        <strong>B</strong>
      </button>
      <button
        type="button"
        class="toolbar-btn"
        :class="{ 'is-active': editor.isActive('italic') }"
        title="Italic"
        @click="editor?.chain().focus().toggleItalic().run()"
      >
        <em>I</em>
      </button>
      <button
        type="button"
        class="toolbar-btn"
        :class="{ 'is-active': editor.isActive('strike') }"
        title="Strikethrough"
        @click="editor?.chain().focus().toggleStrike().run()"
      >
        <s>S</s>
      </button>
      <span class="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />
      <button
        type="button"
        class="toolbar-btn"
        :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
        title="Heading 1"
        @click="editor?.chain().focus().toggleHeading({ level: 1 }).run()"
      >
        <span class="font-semibold text-xs">
          H
          <sub class="text-[0.6rem]">1</sub>
        </span>
      </button>
      <button
        type="button"
        class="toolbar-btn"
        :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
        title="Heading 2"
        @click="editor?.chain().focus().toggleHeading({ level: 2 }).run()"
      >
        <span class="font-semibold text-xs">
          H
          <sub class="text-[0.6rem]">2</sub>
        </span>
      </button>
      <button
        type="button"
        class="toolbar-btn"
        :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
        title="Heading 3"
        @click="editor?.chain().focus().toggleHeading({ level: 3 }).run()"
      >
        <span class="font-semibold text-xs">
          H
          <sub class="text-[0.6rem]">3</sub>
        </span>
      </button>
      <span class="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />
      <button
        type="button"
        class="toolbar-btn"
        :class="{ 'is-active': editor.isActive('bulletList') }"
        title="Bullet List"
        @click="editor?.chain().focus().toggleBulletList().run()"
      >
        •―
      </button>
      <button
        type="button"
        class="toolbar-btn"
        :class="{ 'is-active': editor.isActive('orderedList') }"
        title="Numbered List"
        @click="editor?.chain().focus().toggleOrderedList().run()"
      >
        1.
      </button>
      <span class="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />
      <button
        type="button"
        class="toolbar-btn"
        :class="{ 'is-active': editor.isActive('link') }"
        title="Add Link"
        @click="setLink"
      >
        🔗
      </button>
      <button
        type="button"
        class="toolbar-btn"
        :disabled="!editor.isActive('link')"
        title="Remove Link"
        @click="unsetLink"
      >
        🔗̸
      </button>
      <span class="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />
      <button
        type="button"
        class="toolbar-btn"
        title="Line Break"
        @click.prevent="editor?.chain().focus().setHardBreak().run()"
      >
        ↵
      </button>
      <button
        type="button"
        class="toolbar-btn"
        title="Undo"
        @click="editor?.chain().focus().undo().run()"
      >
        ↩
      </button>
      <button
        type="button"
        class="toolbar-btn"
        title="Redo"
        @click="editor?.chain().focus().redo().run()"
      >
        ↪
      </button>
    </div>
    <EditorContent :editor="editor" />
  </div>
</template>

<style lang="postcss">
.ProseMirror {
  min-height: 80px;
  outline: none;
  @apply text-gray-500 dark:text-gray-300;

  &:focus {
    outline: none;
  }
}
</style>

<style lang="postcss" scoped>
.toolbar-btn {
  @apply flex items-center justify-center min-w-7 h-7 text-sm rounded border border-transparent;
  @apply text-gray-600 dark:text-gray-300 bg-transparent px-1.5;
  @apply hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600;
  @apply disabled:opacity-30 disabled:cursor-not-allowed;
  @apply transition-colors cursor-pointer;

  &.is-active {
    @apply bg-indigo-600 text-white border-indigo-600;
  }

  strong {
    @apply font-bold;
  }

  em {
    @apply italic;
  }

  s {
    @apply line-through;
  }
}
</style>
